import { useCallback, useContext, useEffect, useState } from 'react';
import { Accordion } from '@nimbus-ds/accordion';
import { Box, Card, Text, Link } from '@nimbus-ds/components';
import { CreditCardIcon, InfoCircleIcon } from '@nimbus-ds/icons';
import { DomainOrderResponseDto } from '@tiendanube/common';
import { useStoreName } from 'domains/Auth/hooks';
import {
  CheckoutContext,
  CreditCardForm,
} from 'domains/Billing/Checkout/CheckoutUI';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import { RegistrationInfo, PaymentInfo, SummaryCard } from '..';
import { useDomainCheckoutFlow, useRegistrationForm } from '../../hooks';

interface DomainsCheckoutProps {
  order: DomainOrderResponseDto;
}

export function DomainsCheckout({ order }: Readonly<DomainsCheckoutProps>) {
  const t = useTranslationConfigurations('domains.checkout');
  const storeName = useStoreName();
  const [selectedAccordion, setSelectedAccordion] = useState<string>('0');
  const [processingPaymentStatus, setProcessingPaymentStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const {
    onPay: executeCreditCardForm,
    status: creditCardStatus,
    creditCardPaymentData,
    creditCardFormError,
  } = useContext(CheckoutContext);

  const {
    values,
    errors,
    handleChange,
    handleChangeSelect,
    handleBlur,
    handleValidations,
    isInitialDataLoading,
  } = useRegistrationForm();

  const { startFlow, isExecuting } = useDomainCheckoutFlow({
    payOrderId: order.payOrderId,
    registrationValues: values,
    creditCardStatus,
  });

  useEffect(() => {
    if (creditCardStatus === 'error' || creditCardFormError) {
      setProcessingPaymentStatus('idle');
    }
  }, [creditCardStatus, creditCardFormError, processingPaymentStatus]);

  useEffect(() => {
    if (isExecuting === 'success' && processingPaymentStatus === 'loading') {
      setProcessingPaymentStatus('idle');
    }
  }, [isExecuting, processingPaymentStatus]);

  const handleAccordionChange = useCallback(
    async (newAccordion: string) => {
      if (newAccordion === '1' && selectedAccordion === '0') {
        let isValid = false;

        await handleValidations(() => {
          isValid = true;
        });

        if (isValid) {
          setSelectedAccordion('1');
        }
        return;
      }

      setSelectedAccordion(newAccordion);
    },
    [handleValidations, selectedAccordion],
  );

  const handlePayment = useCallback(async () => {
    let isValid = false;

    await handleValidations(() => {
      isValid = true;
    });

    if (!isValid) {
      setSelectedAccordion('0');
      return;
    }

    if (selectedAccordion === '0') {
      setSelectedAccordion('1');
      return;
    }
    setTimeout(() => {
      setProcessingPaymentStatus('loading');
    }, 200);

    executeCreditCardForm();
    startFlow();
  }, [
    handleValidations,
    setSelectedAccordion,
    executeCreditCardForm,
    startFlow,
    selectedAccordion,
  ]);

  return (
    <Box display="grid" gridTemplateColumns="2fr 1fr" gap="4" marginTop="4">
      <Box display="flex" flexDirection="column" gap="4">
        <CreditCardForm.AlertFailed
          creditCardPaymentData={creditCardPaymentData ?? undefined}
        />
        <Card padding="none">
          <Box
            borderRadius="2"
            borderWidth="1"
            overflow="hidden"
            borderColor="neutral-surfaceHighlight"
          >
            <Accordion
              selectedItem={selectedAccordion}
              onItemSelect={handleAccordionChange}
            >
              <Accordion.Item index="0">
                <Accordion.Header
                  borderTop="none"
                  icon={<InfoCircleIcon size={18} />}
                  title={t('registrationInfo.title')}
                />
                <Accordion.Body>
                  <RegistrationInfo
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    handleChangeSelect={handleChangeSelect}
                    handleBlur={handleBlur}
                    isInitialDataLoading={isInitialDataLoading}
                  />
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item index="1">
                <Accordion.Header
                  icon={<CreditCardIcon size={18} />}
                  title={t('paymentInfo.title')}
                />
                <Accordion.Body>
                  {selectedAccordion === '1' && <PaymentInfo />}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Box>
        </Card>
        <Text fontSize="caption" color="neutral-textLow">
          {t('termsNotice')}{' '}
          <Link
            as="a"
            href="https://www.icann.org/es/contracted-parties/consensus-policies/uniform-domain-name-dispute-resolution-policy/uniform-domain-name-dispute-resolution-policy-01-01-2020-es"
            target="_blank"
            appearance="primary"
            textDecoration="underline"
          >
            {t('icannPolicies')}
          </Link>{' '}
          {t('termsAnd')}{' '}
          <Link
            as="a"
            href="https://www.tiendanube.com/mx/terminos-de-uso/master-domain-registration"
            target="_blank"
            appearance="primary"
            textDecoration="underline"
          >
            {t('registrationContract')}
          </Link>{' '}
          {t('sponsoredBy')}
        </Text>
      </Box>
      <Box>
        <SummaryCard
          storeName={storeName}
          domainName={order.domainName}
          price={order.price}
          onPayment={handlePayment}
          isLoading={processingPaymentStatus === 'loading'}
        />
      </Box>
    </Box>
  );
}
